import { createApi } from '@reduxjs/toolkit/query/react';
import { appPortalBaseQuery } from '@/features/baseQuery.ts';
import {
  AuditFieldDecryptionStatus,
  AuditLogDetail,
  AuditLogFieldChange,
  AuditLogListResponse,
  AuditLogSummary,
  BackendAuditFieldDecryptionStatus,
  BackendAuditLogDetailDto,
  BackendAuditLogFieldChangeDto,
  BackendAuditLogListDto,
  BackendAuditLogSummaryDto,
  GetAuditLogsRequest,
} from '@/features/audit-logs/audit-logs.def.ts';

const toOptionalString = (value?: string | null) => value ?? undefined;

const toOptionalJsonValue = <T>(value?: T | null) =>
  value === undefined ? undefined : (value ?? null);

const normalizeDecryptionStatus = (
  value?: BackendAuditFieldDecryptionStatus | null,
): AuditFieldDecryptionStatus | undefined => {
  switch (value) {
    case 'NotRequested':
    case 0:
      return 'NotRequested';
    case 'Decrypted':
    case 1:
      return 'Decrypted';
    case 'Unavailable':
    case 2:
      return 'Unavailable';
    default:
      return undefined;
  }
};

const transformAuditLogSummary = (dto: BackendAuditLogSummaryDto): AuditLogSummary => ({
  auditId: dto.auditId,
  changedAtUtc: dto.changedAtUtc,
  transactionId: dto.transactionId,
  schemaName: dto.schemaName,
  tableName: dto.tableName,
  operation: dto.operation,
  rowPk: dto.rowPk,
  actorUserId: toOptionalString(dto.actorUserId),
  actorUsername: toOptionalString(dto.actorUsername),
  requestId: toOptionalString(dto.requestId),
  source: toOptionalString(dto.source),
  sourceType: toOptionalString(dto.sourceType),
  jobId: toOptionalString(dto.jobId),
  reason: toOptionalString(dto.reason),
  changedFields: dto.changedFields,
  changedFieldCount: dto.changedFieldCount,
  hasOldData: dto.hasOldData,
  hasNewData: dto.hasNewData,
});

const transformAuditLogFieldChange = (dto: BackendAuditLogFieldChangeDto): AuditLogFieldChange => ({
  fieldName: dto.fieldName,
  oldValue: dto.oldValue === undefined ? undefined : dto.oldValue,
  newValue: dto.newValue === undefined ? undefined : dto.newValue,
  isEncrypted: dto.isEncrypted ?? false,
  decryptionStatus: normalizeDecryptionStatus(dto.decryptionStatus),
  decryptedOldValue: toOptionalJsonValue(dto.decryptedOldValue),
  decryptedNewValue: toOptionalJsonValue(dto.decryptedNewValue),
});

const transformAuditLogDetail = (dto: BackendAuditLogDetailDto): AuditLogDetail => ({
  auditId: dto.auditId,
  changedAtUtc: dto.changedAtUtc,
  transactionId: dto.transactionId,
  databaseName: dto.databaseName,
  schemaName: dto.schemaName,
  tableName: dto.tableName,
  operation: dto.operation,
  rowPk: dto.rowPk,
  dbUser: dto.dbUser,
  sessionUserName: dto.sessionUserName,
  applicationName: toOptionalString(dto.applicationName),
  clientAddress: toOptionalString(dto.clientAddress),
  actorUserId: toOptionalString(dto.actorUserId),
  actorUsername: toOptionalString(dto.actorUsername),
  requestId: toOptionalString(dto.requestId),
  source: toOptionalString(dto.source),
  sourceType: toOptionalString(dto.sourceType),
  tenantId: toOptionalString(dto.tenantId),
  jobId: toOptionalString(dto.jobId),
  reason: toOptionalString(dto.reason),
  impersonatorUsername: toOptionalString(dto.impersonatorUsername),
  context: dto.context,
  oldData: dto.oldData ?? undefined,
  newData: dto.newData ?? undefined,
  changedFields: dto.changedFields,
  fieldChanges: dto.fieldChanges.map(transformAuditLogFieldChange),
});

export const auditLogsApi = createApi({
  reducerPath: 'auditLogsApi',
  baseQuery: appPortalBaseQuery(),
  tagTypes: ['AuditLog'],
  endpoints: (builder) => ({
    getAuditLogs: builder.query<AuditLogListResponse, GetAuditLogsRequest | void>({
      query: (params) => ({
        url: '/audit-logs',
        method: 'GET',
        params: params ?? undefined,
      }),
      transformResponse: (response: BackendAuditLogListDto): AuditLogListResponse => ({
        items: response.items.map(transformAuditLogSummary),
        totalCount: response.totalCount,
        page: response.page,
        pageSize: response.pageSize,
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'AuditLog', id: 'LIST' },
              ...result.items.map((item) => ({ type: 'AuditLog' as const, id: item.auditId })),
            ]
          : [{ type: 'AuditLog', id: 'LIST' }],
    }),
    getAuditLogDetail: builder.query<AuditLogDetail, number>({
      query: (auditId) => ({
        url: `/audit-logs/${auditId}`,
        method: 'GET',
      }),
      transformResponse: transformAuditLogDetail,
      providesTags: (_result, _error, auditId) => [{ type: 'AuditLog', id: auditId }],
    }),
    getDecryptedAuditLogDetail: builder.query<AuditLogDetail, number>({
      query: (auditId) => ({
        url: `/audit-logs/${auditId}/decrypted`,
        method: 'GET',
      }),
      transformResponse: transformAuditLogDetail,
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, auditId) => [{ type: 'AuditLog', id: auditId }],
    }),
  }),
});

export const {
  useGetAuditLogsQuery,
  useGetAuditLogDetailQuery,
  useGetDecryptedAuditLogDetailQuery,
} = auditLogsApi;
